import React from "react";
import Skeleton from "./Skeleton";
import styles from "./skeleton.module.scss";

// ============================================================================
// Shared skeleton layout components for full-page loading states.
// Each skeleton mirrors the actual page layout so the user sees a
// content-shaped placeholder instead of a circular spinner.
// ============================================================================

// ---- helpers ---------------------------------------------------------------
const Row: React.FC<{
  gap?: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}> = ({ gap = 12, children, style, className }) => (
  <div className={className} style={{ display: "flex", gap, ...style }}>{children}</div>
);

const Col: React.FC<{
  gap?: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}> = ({ gap = 8, children, style, className }) => (
  <div className={className} style={{ display: "flex", flexDirection: "column", gap, ...style }}>
    {children}
  </div>
);

const Card: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ children, style }) => (
  <div
    style={{
      background: "var(--color-bg-surface)",
      borderRadius: 12,
      padding: 20,
      boxShadow: "0 1px 3px var(--color-shadow)",
      ...style,
    }}
  >
    {children}
  </div>
);

// ---- Course List Page (grid of course cards) --------------------------------
export const CoursesPageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Section header */}
    <div style={{ marginBottom: 40 }}>
      <Row gap={8} style={{ alignItems: "center", marginBottom: 20 }}>
        <Skeleton width={24} height={24} borderRadius="4px" />
        <Skeleton width="30%" height={24} borderRadius="4px" />
      </Row>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
          gap: 24,
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <Skeleton height={160} borderRadius="8px" />
            <Col gap={8} style={{ marginTop: 12 }}>
              <Skeleton width="70%" height={20} borderRadius="4px" />
              <Skeleton width="90%" height={14} borderRadius="4px" />
              <Skeleton width="50%" height={14} borderRadius="4px" />
              <Skeleton width="30%" height={32} borderRadius="16px" />
            </Col>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

// ---- Admin Courses Page (similar grid with stat cards on top) ---------------
export const AdminCoursesPageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Stat cards row */}
    <Row gap={16} style={{ marginBottom: 24 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} style={{ flex: 1 }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={40} height={40} borderRadius="10px" />
            <Col gap={4}>
              <Skeleton width={60} height={24} borderRadius="4px" />
              <Skeleton width={80} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
      ))}
    </Row>
    {/* Section header */}
    <Row gap={8} style={{ justifyContent: "space-between", marginBottom: 20 }}>
      <Skeleton width="20%" height={24} borderRadius="4px" />
      <Row gap={8}>
        <Skeleton width={180} height={36} borderRadius="8px" />
        <Skeleton width={120} height={36} borderRadius="8px" />
      </Row>
    </Row>
    {/* Course cards grid */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 24,
      }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <Skeleton height={160} borderRadius="8px" />
          <Col gap={8} style={{ marginTop: 12 }}>
            <Skeleton width="70%" height={20} borderRadius="4px" />
            <Skeleton width="90%" height={14} borderRadius="4px" />
            <Row gap={8}>
              <Skeleton width={60} height={24} borderRadius="12px" />
              <Skeleton width={60} height={24} borderRadius="12px" />
            </Row>
          </Col>
        </Card>
      ))}
    </div>
  </div>
);

// ---- Course Detail Page (header + module list) ------------------------------
export const CourseDetailSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Breadcrumbs */}
    <Row gap={6} style={{ marginBottom: 20 }}>
      <Skeleton width={60} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={80} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={120} height={14} borderRadius="4px" />
    </Row>
    {/* Header */}
    <Row gap={16} style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 24 }} className={styles.responsiveRow}>
      <Col gap={6}>
        <Skeleton width="70%" height={28} borderRadius="4px" />
        <Skeleton width="40%" height={16} borderRadius="4px" />
      </Col>
      <Row gap={8} className={styles.hideOnMobile}>
        <Skeleton width={100} height={36} borderRadius="8px" />
        <Skeleton width={120} height={36} borderRadius="8px" />
      </Row>
    </Row>
    {/* Stats */}
    <Row gap={16} style={{ marginBottom: 24 }} className={styles.responsiveRow}>
      {Array.from({ length: 2 }).map((_, i) => (
        <Card key={i} style={{ flex: 1 }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={48} height={48} borderRadius="12px" />
            <Col gap={4}>
              <Skeleton width={40} height={24} borderRadius="4px" />
              <Skeleton width={80} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
      ))}
    </Row>
    {/* Module list */}
    <Col gap={0} style={{ marginBottom: 16 }}>
      <Skeleton width="20%" height={22} borderRadius="4px" />
    </Col>
    <Col gap={12}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <Row gap={16} style={{ alignItems: "center" }}>
            <Skeleton width={32} height={20} borderRadius="4px" />
            <Skeleton width={40} height={40} borderRadius="10px" />
            <Col gap={4} style={{ flex: 1 }}>
              <Skeleton width="60%" height={18} borderRadius="4px" />
              <Skeleton width="40%" height={14} borderRadius="4px" />
            </Col>
            <Skeleton width={70} height={24} borderRadius="12px" />
          </Row>
        </Card>
      ))}
    </Col>
  </div>
);

// ---- Module Page (admin: video + exercises, learner: similar) ----------------
export const ModulePageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Breadcrumbs */}
    <Row gap={6} style={{ marginBottom: 20 }}>
      <Skeleton width={50} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={70} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={100} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={120} height={14} borderRadius="4px" />
    </Row>
    {/* Two column layout */}
    <Row gap={24} style={{ alignItems: "flex-start" }} className={styles.responsiveRow}>
      {/* Left column */}
      <Col gap={20} style={{ flex: 2, minWidth: 0 }}>
        {/* Video placeholder */}
        <Skeleton height={300} borderRadius="12px" />
        {/* Title + meta */}
        <Col gap={8}>
          <Skeleton width="70%" height={24} borderRadius="4px" />
          <Skeleton width="30%" height={16} borderRadius="4px" />
        </Col>
        {/* Tabs */}
        <Row gap={16}>
          <Skeleton width="25%" height={32} borderRadius="8px" />
          <Skeleton width="25%" height={32} borderRadius="8px" />
          <Skeleton width="30%" height={32} borderRadius="8px" />
        </Row>
        {/* Tab content */}
        <Col gap={6}>
          <Skeleton height={14} borderRadius="4px" />
          <Skeleton height={14} borderRadius="4px" />
          <Skeleton width="80%" height={14} borderRadius="4px" />
        </Col>
      </Col>
      {/* Right column */}
      <Col gap={16} style={{ flex: 1, minWidth: 0 }}>
        {/* Progress card */}
        <Card>
          <Col gap={8}>
            <Skeleton width="50%" height={18} borderRadius="4px" />
            <Skeleton height={8} borderRadius="4px" />
            <Row gap={0} style={{ justifyContent: "space-between" }}>
              <Skeleton width="60%" height={14} borderRadius="4px" />
              <Skeleton width={30} height={14} borderRadius="4px" />
            </Row>
          </Col>
        </Card>
        {/* Exercise list */}
        <Card>
          <Col gap={8}>
            <Row gap={8} style={{ alignItems: "center" }}>
              <Skeleton width="40%" height={18} borderRadius="4px" />
              <Skeleton width={30} height={18} borderRadius="4px" />
            </Row>
            {Array.from({ length: 5 }).map((_, i) => (
              <Row key={i} gap={12} style={{ alignItems: "center", padding: "8px 0" }}>
                <Skeleton width={32} height={32} borderRadius="50%" />
                <Col gap={4} style={{ flex: 1 }}>
                  <Skeleton width="70%" height={16} borderRadius="4px" />
                  <Skeleton width="40%" height={12} borderRadius="4px" />
                </Col>
                <Skeleton width={20} height={20} borderRadius="4px" />
              </Row>
            ))}
          </Col>
        </Card>
      </Col>
    </Row>
  </div>
);

// ---- Exercise Page ----------------------------------------------------------
export const ExercisePageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Breadcrumbs */}
    <Row gap={6} style={{ marginBottom: 16 }}>
      <Skeleton width={50} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={70} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={90} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={120} height={14} borderRadius="4px" />
    </Row>
    <Row gap={24} style={{ alignItems: "flex-start" }} className={styles.responsiveRow}>
      {/* Exercise body */}
      <Col gap={16} style={{ flex: 2, minWidth: 0 }}>
        <Card style={{ minHeight: 400 }}>
          <Col gap={12}>
            <Skeleton width="50%" height={22} borderRadius="4px" />
            <Skeleton height={14} borderRadius="4px" />
            <Skeleton height={14} borderRadius="4px" />
            <Skeleton width="90%" height={14} borderRadius="4px" />
            <div style={{ height: 16 }} />
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={44} borderRadius="8px" />
            ))}
            <div style={{ height: 16 }} />
            <Skeleton width="60%" height={14} borderRadius="4px" />
            <Skeleton height={14} borderRadius="4px" />
          </Col>
        </Card>
      </Col>
      {/* Sidebar */}
      <Col gap={16} style={{ flex: 1, minWidth: 0 }}>
        <Card>
          <Col gap={8}>
            <Skeleton width="50%" height={16} borderRadius="4px" />
            <Skeleton height={8} borderRadius="4px" />
            <Skeleton width="70%" height={14} borderRadius="4px" />
          </Col>
        </Card>
        <Card>
          <Col gap={6}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Row key={i} gap={8} style={{ alignItems: "center" }}>
                <Skeleton width={24} height={24} borderRadius="50%" />
                <Skeleton width="60%" height={14} borderRadius="4px" />
              </Row>
            ))}
          </Col>
        </Card>
      </Col>
    </Row>
  </div>
);

// ---- Dashboard / Home -------------------------------------------------------
export const DashboardSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Welcome */}
    <Col gap={6} style={{ marginBottom: 24 }}>
      <Skeleton width="40%" height={28} borderRadius="4px" />
      <Skeleton width="50%" height={16} borderRadius="4px" />
    </Col>
    {/* Stat cards row */}
    <Row gap={16} style={{ marginBottom: 24 }} className={styles.wrapRow}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} style={{ flex: "1 1 140px" }}>
          <Col gap={8} style={{ alignItems: "center", padding: "8px 0" }}>
            <Skeleton width={40} height={40} borderRadius="50%" />
            <Skeleton width={40} height={24} borderRadius="4px" />
            <Skeleton width={80} height={14} borderRadius="4px" />
          </Col>
        </Card>
      ))}
    </Row>
    {/* Charts */}
    <Row gap={16} style={{ marginBottom: 24 }} className={styles.responsiveRow}>
      <Card style={{ flex: 1 }}>
        <Col gap={8}>
          <Skeleton width="30%" height={18} borderRadius="4px" />
          <Skeleton height={200} borderRadius="8px" />
        </Col>
      </Card>
      <Card style={{ flex: 1 }}>
        <Col gap={8}>
          <Skeleton width="30%" height={18} borderRadius="4px" />
          <Skeleton height={200} borderRadius="8px" />
        </Col>
      </Card>
    </Row>
    {/* Progress row */}
    <Row gap={16} className={styles.responsiveRow}>
      <Card style={{ flex: 1 }}>
        <Col gap={8} style={{ alignItems: "center" }}>
          <Skeleton width="40%" height={18} borderRadius="4px" />
          <Skeleton width={140} height={140} borderRadius="50%" />
        </Col>
      </Card>
      <Card style={{ flex: 1 }}>
        <Col gap={8}>
          <Skeleton width="40%" height={18} borderRadius="4px" />
          <Skeleton height={12} borderRadius="4px" />
          <Skeleton height={12} borderRadius="4px" />
          <Skeleton height={12} borderRadius="4px" />
        </Col>
      </Card>
    </Row>
  </div>
);

// ---- Admin Dashboard --------------------------------------------------------
export const AdminDashboardSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    <Skeleton width="30%" height={28} borderRadius="4px" />
    <div style={{ height: 20 }} />
    {/* Stat cards */}
    <Row gap={16} style={{ marginBottom: 24 }} className={styles.wrapRow}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} style={{ flex: "1 1 140px" }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={44} height={44} borderRadius="12px" />
            <Col gap={4}>
              <Skeleton width={50} height={24} borderRadius="4px" />
              <Skeleton width={70} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
      ))}
    </Row>
    {/* Charts + tables */}
    <Row gap={16} style={{ marginBottom: 24 }} className={styles.responsiveRow}>
      <Card style={{ flex: 2 }}>
        <Skeleton width="40%" height={18} borderRadius="4px" />
        <div style={{ height: 8 }} />
        <Skeleton height={180} borderRadius="8px" />
      </Card>
      <Card style={{ flex: 1 }}>
        <Skeleton width="40%" height={18} borderRadius="4px" />
        <div style={{ height: 8 }} />
        <Col gap={6}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={24} borderRadius="4px" />
          ))}
        </Col>
      </Card>
    </Row>
    {/* Bottom row */}
    <Row gap={16} className={styles.responsiveRow}>
      <Card style={{ flex: 1 }}>
        <Skeleton width="40%" height={18} borderRadius="4px" />
        <div style={{ height: 8 }} />
        <Col gap={6}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={20} borderRadius="4px" />
          ))}
        </Col>
      </Card>
      <Card style={{ flex: 1 }}>
        <Skeleton width="40%" height={18} borderRadius="4px" />
        <div style={{ height: 8 }} />
        <Col gap={6}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={20} borderRadius="4px" />
          ))}
        </Col>
      </Card>
    </Row>
  </div>
);

// ---- Vocabulary Review Page -------------------------------------------------
export const VocabReviewSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Header */}
    <Row gap={12} style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
      <Col gap={6}>
        <Skeleton width={200} height={28} borderRadius="4px" />
        <Skeleton width={120} height={16} borderRadius="4px" />
      </Col>
      <Row gap={8}>
        <Skeleton width={100} height={36} borderRadius="8px" />
        <Skeleton width={100} height={36} borderRadius="8px" />
      </Row>
    </Row>
    {/* Stats row */}
    <Row gap={16} style={{ marginBottom: 24 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} style={{ flex: 1 }}>
          <Col gap={6} style={{ alignItems: "center" }}>
            <Skeleton width={40} height={40} borderRadius="50%" />
            <Skeleton width={40} height={22} borderRadius="4px" />
            <Skeleton width={60} height={14} borderRadius="4px" />
          </Col>
        </Card>
      ))}
    </Row>
    {/* Word list / flashcard area */}
    <Card>
      <Col gap={8}>
        <Row gap={8} style={{ justifyContent: "space-between", marginBottom: 8 }}>
          <Skeleton width={200} height={36} borderRadius="8px" />
          <Skeleton width={120} height={36} borderRadius="8px" />
        </Row>
        {Array.from({ length: 6 }).map((_, i) => (
          <Row key={i} gap={12} style={{ alignItems: "center", padding: "8px 0" }}>
            <Skeleton width={36} height={36} borderRadius="8px" />
            <Col gap={4} style={{ flex: 1 }}>
              <Skeleton width="40%" height={16} borderRadius="4px" />
              <Skeleton width="25%" height={12} borderRadius="4px" />
            </Col>
            <Skeleton width={60} height={24} borderRadius="12px" />
          </Row>
        ))}
      </Col>
    </Card>
  </div>
);

// ---- User List (admin) — table with search ----------------------------------
export const UserListSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Meta row */}
    <Row gap={16} style={{ marginBottom: 12 }}>
      <Skeleton width={100} height={14} borderRadius="4px" />
      <Skeleton width={120} height={14} borderRadius="4px" />
      <Skeleton width={100} height={14} borderRadius="4px" />
    </Row>
    {/* Search */}
    <Skeleton height={40} borderRadius="8px" />
    <div style={{ height: 16 }} />
    {/* Table rows */}
    <Col gap={2}>
      {Array.from({ length: 10 }).map((_, i) => (
        <Row key={i} gap={16} style={{ padding: "10px 0", alignItems: "center" }}>
          <Skeleton width={36} height={36} borderRadius="50%" />
          <Skeleton width="25%" height={16} borderRadius="4px" />
          <Skeleton width="30%" height={14} borderRadius="4px" />
          <Skeleton width={60} height={24} borderRadius="12px" />
          <Skeleton width={40} height={14} borderRadius="4px" />
        </Row>
      ))}
    </Col>
  </div>
);

// ---- Profile Dashboard (left: card + right: course grid) --------------------
export const ProfileSkeleton: React.FC = () => (
  <Row gap={24} style={{ width: "100%", alignItems: "flex-start" }}>
    {/* Left column */}
    <Col gap={16} style={{ flex: 1, maxWidth: 340 }}>
      <Card>
        <Col gap={12} style={{ alignItems: "center" }}>
          <Skeleton width={80} height={80} borderRadius="50%" />
          <Skeleton width="60%" height={22} borderRadius="4px" />
          <Skeleton width="40%" height={14} borderRadius="4px" />
          <div style={{ height: 8 }} />
          <Row gap={16} style={{ width: "100%" }}>
            <Col gap={4} style={{ flex: 1, alignItems: "center" }}>
              <Skeleton width={30} height={22} borderRadius="4px" />
              <Skeleton width={60} height={12} borderRadius="4px" />
            </Col>
            <Col gap={4} style={{ flex: 1, alignItems: "center" }}>
              <Skeleton width={30} height={22} borderRadius="4px" />
              <Skeleton width={60} height={12} borderRadius="4px" />
            </Col>
          </Row>
        </Col>
      </Card>
    </Col>
    {/* Right column */}
    <Col gap={16} style={{ flex: 2 }}>
      <Row gap={16}>
        <Card style={{ flex: 1 }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={60} height={60} borderRadius="50%" />
            <Col gap={4}>
              <Skeleton width={30} height={24} borderRadius="4px" />
              <Skeleton width={100} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
        <Card style={{ flex: 1 }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={60} height={60} borderRadius="50%" />
            <Col gap={4}>
              <Skeleton width={30} height={24} borderRadius="4px" />
              <Skeleton width={100} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
      </Row>
      <Card>
        <Skeleton width="30%" height={18} borderRadius="4px" />
        <div style={{ height: 12 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
              <Row gap={12} style={{ alignItems: "center" }}>
                <Skeleton width={48} height={48} borderRadius="50%" />
                <Col gap={4} style={{ flex: 1 }}>
                  <Skeleton width="40%" height={12} borderRadius="4px" />
                  <Skeleton width="70%" height={16} borderRadius="4px" />
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      </Card>
    </Col>
  </Row>
);

// ---- Activity Page ----------------------------------------------------------
export const ActivityPageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Welcome */}
    <Col gap={6} style={{ marginBottom: 24 }}>
      <Skeleton width="40%" height={28} borderRadius="4px" />
      <Skeleton width="50%" height={16} borderRadius="4px" />
    </Col>
    {/* Continue Learning */}
    <Card style={{ marginBottom: 24 }}>
      <Row gap={16} style={{ alignItems: "center" }}>
        <Skeleton width={80} height={80} borderRadius="12px" />
        <Col gap={6} style={{ flex: 1 }}>
          <Skeleton width="50%" height={18} borderRadius="4px" />
          <Skeleton width="30%" height={14} borderRadius="4px" />
          <Skeleton width={120} height={32} borderRadius="8px" />
        </Col>
      </Row>
    </Card>
    {/* Stats */}
    <Row gap={16} style={{ marginBottom: 24 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} style={{ flex: 1 }}>
          <Col gap={8} style={{ alignItems: "center", padding: "8px 0" }}>
            <Skeleton width={40} height={40} borderRadius="50%" />
            <Skeleton width={40} height={22} borderRadius="4px" />
            <Skeleton width={80} height={14} borderRadius="4px" />
          </Col>
        </Card>
      ))}
    </Row>
    {/* Charts */}
    <Row gap={16} style={{ marginBottom: 24 }}>
      <Card style={{ flex: 1 }}>
        <Skeleton height={180} borderRadius="8px" />
      </Card>
      <Card style={{ flex: 1 }}>
        <Skeleton height={180} borderRadius="8px" />
      </Card>
    </Row>
    {/* Timeline */}
    <Card>
      <Skeleton width="30%" height={18} borderRadius="4px" />
      <div style={{ height: 12 }} />
      <Col gap={12}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Row key={i} gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={32} height={32} borderRadius="50%" />
            <Col gap={4} style={{ flex: 1 }}>
              <Skeleton width="60%" height={14} borderRadius="4px" />
              <Skeleton width="40%" height={12} borderRadius="4px" />
            </Col>
            <Skeleton width={50} height={14} borderRadius="4px" />
          </Row>
        ))}
      </Col>
    </Card>
  </div>
);

// ---- Curator Dashboard (stats + two-column cards) ---------------------------
export const CuratorDashboardSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    <Skeleton width="40%" height={28} borderRadius="4px" />
    <div style={{ height: 20 }} />
    <Row gap={16} style={{ marginBottom: 24 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} style={{ flex: 1 }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={44} height={44} borderRadius="12px" />
            <Col gap={4}>
              <Skeleton width={40} height={24} borderRadius="4px" />
              <Skeleton width={80} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
      ))}
    </Row>
    <Row gap={16}>
      <Card style={{ flex: 1 }}>
        <Skeleton width="40%" height={18} borderRadius="4px" />
        <div style={{ height: 12 }} />
        <Col gap={10}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Row key={i} gap={12} style={{ alignItems: "center" }}>
              <Skeleton width={24} height={20} borderRadius="4px" />
              <Skeleton width={36} height={36} borderRadius="50%" />
              <Col gap={4} style={{ flex: 1 }}>
                <Skeleton width="60%" height={14} borderRadius="4px" />
                <Skeleton width="40%" height={12} borderRadius="4px" />
              </Col>
            </Row>
          ))}
        </Col>
      </Card>
      <Card style={{ flex: 1 }}>
        <Skeleton width="40%" height={18} borderRadius="4px" />
        <div style={{ height: 12 }} />
        <Col gap={10}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Row key={i} gap={12} style={{ alignItems: "center" }}>
              <Skeleton width={8} height={8} borderRadius="50%" />
              <Col gap={4} style={{ flex: 1 }}>
                <Skeleton width="50%" height={14} borderRadius="4px" />
                <Skeleton width="70%" height={12} borderRadius="4px" />
              </Col>
              <Skeleton width={60} height={12} borderRadius="4px" />
            </Row>
          ))}
        </Col>
      </Card>
    </Row>
  </div>
);

// ---- Learner Course Page (hero + module accordion) --------------------------
export const LearnerCoursePageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Breadcrumbs */}
    <Row gap={6} style={{ marginBottom: 20 }}>
      <Skeleton width={50} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={80} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={130} height={14} borderRadius="4px" />
    </Row>
    {/* Hero section */}
    <Card style={{ marginBottom: 24 }}>
      <Row gap={24} style={{ alignItems: "center" }}>
        <Skeleton width={200} height={150} borderRadius="12px" />
        <Col gap={10} style={{ flex: 1 }}>
          <Skeleton width="60%" height={28} borderRadius="4px" />
          <Skeleton width="80%" height={14} borderRadius="4px" />
          <Skeleton width="40%" height={14} borderRadius="4px" />
          <Row gap={12}>
            <Skeleton width={100} height={36} borderRadius="8px" />
            <Skeleton width={100} height={36} borderRadius="8px" />
          </Row>
        </Col>
      </Row>
    </Card>
    {/* Module list */}
    <Col gap={12}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Card key={i}>
          <Row gap={16} style={{ alignItems: "center" }}>
            <Skeleton width={40} height={40} borderRadius="50%" />
            <Col gap={4} style={{ flex: 1 }}>
              <Skeleton width="50%" height={18} borderRadius="4px" />
              <Skeleton width="30%" height={14} borderRadius="4px" />
            </Col>
            <Skeleton width={60} height={24} borderRadius="12px" />
            <Skeleton width={24} height={24} borderRadius="4px" />
          </Row>
        </Card>
      ))}
    </Col>
  </div>
);

// ---- Admin Table Page (logs, reviews, bugs, etc.) ---------------------------
export const AdminTableSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Header */}
    <Row gap={12} style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
      <Skeleton width="25%" height={28} borderRadius="4px" />
      <Row gap={8}>
        <Skeleton width={180} height={36} borderRadius="8px" />
        <Skeleton width={100} height={36} borderRadius="8px" />
      </Row>
    </Row>
    {/* Table header */}
    <Row gap={0} style={{ padding: "12px 16px", background: "var(--color-bg-subtle)", borderRadius: "8px 8px 0 0" }}>
      <Skeleton width="20%" height={14} borderRadius="4px" />
      <Skeleton width="30%" height={14} borderRadius="4px" />
      <Skeleton width="15%" height={14} borderRadius="4px" />
      <Skeleton width="15%" height={14} borderRadius="4px" />
    </Row>
    {/* Table rows */}
    <Col gap={0}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Row key={i} gap={0} style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
          <Skeleton width="20%" height={14} borderRadius="4px" />
          <Skeleton width="30%" height={14} borderRadius="4px" />
          <Skeleton width="15%" height={14} borderRadius="4px" />
          <Skeleton width="15%" height={14} borderRadius="4px" />
        </Row>
      ))}
    </Col>
  </div>
);

// ---- Students Page (admin) --------------------------------------------------
export const StudentsPageSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Header with stats */}
    <Row gap={16} style={{ marginBottom: 24 }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} style={{ flex: 1 }}>
          <Row gap={12} style={{ alignItems: "center" }}>
            <Skeleton width={40} height={40} borderRadius="10px" />
            <Col gap={4}>
              <Skeleton width={40} height={22} borderRadius="4px" />
              <Skeleton width={80} height={14} borderRadius="4px" />
            </Col>
          </Row>
        </Card>
      ))}
    </Row>
    {/* Search + filters */}
    <Row gap={12} style={{ marginBottom: 16 }}>
      <Skeleton width={300} height={40} borderRadius="8px" />
      <Skeleton width={120} height={40} borderRadius="8px" />
    </Row>
    {/* Table rows */}
    <Col gap={2}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Row key={i} gap={16} style={{ padding: "12px 0", alignItems: "center" }}>
          <Skeleton width={40} height={40} borderRadius="50%" />
          <Col gap={4} style={{ flex: 1 }}>
            <Skeleton width="30%" height={16} borderRadius="4px" />
            <Skeleton width="20%" height={12} borderRadius="4px" />
          </Col>
          <Skeleton width={60} height={24} borderRadius="12px" />
          <Skeleton width={80} height={14} borderRadius="4px" />
        </Row>
      ))}
    </Col>
  </div>
);

// ---- User Details Page (admin) -----------------------------------------------
export const UserDetailsSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Breadcrumbs */}
    <Row gap={6} style={{ marginBottom: 20 }}>
      <Skeleton width={70} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={50} height={14} borderRadius="4px" />
      <Skeleton width={8} height={14} borderRadius="4px" />
      <Skeleton width={120} height={14} borderRadius="4px" />
    </Row>
    {/* Profile header */}
    <Card style={{ marginBottom: 24 }}>
      <Row gap={16} style={{ alignItems: "center" }}>
        <Skeleton width={64} height={64} borderRadius="50%" />
        <Col gap={4} style={{ flex: 1 }}>
          <Skeleton width="30%" height={24} borderRadius="4px" />
          <Skeleton width="20%" height={14} borderRadius="4px" />
        </Col>
        <Skeleton width={80} height={32} borderRadius="8px" />
      </Row>
    </Card>
    {/* Tabs */}
    <Row gap={8} style={{ marginBottom: 20 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} width={80} height={36} borderRadius="8px" />
      ))}
    </Row>
    {/* Content area */}
    <Card>
      <Col gap={12}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height={20} borderRadius="4px" />
        ))}
      </Col>
    </Card>
  </div>
);

// ---- Payment Confirmation Page -----------------------------------------------
export const PaymentPageSkeleton: React.FC = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 400,
      width: "100%",
    }}
  >
    <Card style={{ textAlign: "center", padding: 40, maxWidth: 400, width: "100%" }}>
      <Col gap={16} style={{ alignItems: "center" }}>
        <Skeleton width={80} height={80} borderRadius="50%" />
        <Skeleton width="70%" height={24} borderRadius="4px" />
        <Skeleton width="50%" height={16} borderRadius="4px" />
        <div style={{ height: 8 }} />
        <Skeleton width={200} height={40} borderRadius="8px" />
      </Col>
    </Card>
  </div>
);

// ---- Admin Sub-panel Loading (attempts, exercises, modules, courses) ---------
export const AdminPanelSkeleton: React.FC = () => (
  <Col gap={8} style={{ padding: "12px 0" }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Row key={i} gap={12} style={{ alignItems: "center" }}>
        <Skeleton width={24} height={24} borderRadius="4px" />
        <Skeleton width="40%" height={14} borderRadius="4px" />
        <Skeleton width="20%" height={14} borderRadius="4px" />
        <Skeleton width={60} height={14} borderRadius="4px" />
      </Row>
    ))}
  </Col>
);

// ---- Admin Courses Container (old admin, simple list) ------------------------
export const AdminCoursesContainerSkeleton: React.FC = () => (
  <div style={{ width: "100%" }}>
    {/* Breadcrumbs + buttons */}
    <Row gap={12} style={{ justifyContent: "space-between", marginBottom: 20 }}>
      <Row gap={6}>
        <Skeleton width={50} height={14} borderRadius="4px" />
        <Skeleton width={8} height={14} borderRadius="4px" />
        <Skeleton width={70} height={14} borderRadius="4px" />
      </Row>
      <Row gap={8}>
        <Skeleton width={120} height={36} borderRadius="8px" />
        <Skeleton width={100} height={36} borderRadius="8px" />
      </Row>
    </Row>
    {/* Course list */}
    <Col gap={12}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Card key={i}>
          <Row gap={16} style={{ alignItems: "center" }}>
            <Skeleton width={60} height={60} borderRadius="8px" />
            <Col gap={4} style={{ flex: 1 }}>
              <Skeleton width="50%" height={18} borderRadius="4px" />
              <Skeleton width="30%" height={14} borderRadius="4px" />
            </Col>
            <Skeleton width={60} height={24} borderRadius="12px" />
          </Row>
        </Card>
      ))}
    </Col>
  </div>
);
